import type { IncomeSchedule, Transaction } from './db';
import { calculateCategorySummary, DEBT_TYPES } from './db';
import { getCurrentMonth, getPayCycleRange } from './month';
import { formatDate, parseDateOnly, round2 } from './format';
import { UNCAT } from './category';

// Re-exported for existing importers (the budgets page, budget.test.ts); the
// canonical definition lives in ./format.
export { formatDate };

export type BudgetStatus = {
	isOverBudget: boolean;
	isUnbudgeted: boolean;
	isFullySpent: boolean;
	isUnderBudget: boolean;
	statusGroup: number;
};

type BudgetStatusInput = {
	budgeted: number;
	spent: number;
	percentUsed: number;
	hasBudgetActivity: boolean;
	isNewBudget: boolean;
};

export type CategoryBudgetItem = {
	name: string;
	budgeted: number;
	spent: number;
	count: number;
	percentUsed: number;
	isUncategorised: boolean;
	isOverBudget: boolean;
	isUnbudgeted: boolean;
	isFullySpent: boolean;
	isUnderBudget: boolean;
	statusGroup: number; // 0 = over/unbudgeted, 1 = fully spent, 2 = under budget
};

/**
 * Sorts budget items and partitions them into the active list and the
 * fully-spent list. Sorting by status groups over/unbudgeted first, then
 * under-budget, with spent amount descending as a stable tiebreaker; sorting by
 * name is a plain alphabetical sort. The input array is not mutated.
 */
export function orderBudgetItems(
	items: CategoryBudgetItem[],
	sortBy: 'status' | 'name'
): { active: CategoryBudgetItem[]; fullySpent: CategoryBudgetItem[] } {
	const sorted = [...items];
	if (sortBy === 'name') {
		sorted.sort((a, b) => a.name.localeCompare(b.name));
	} else {
		sorted.sort((a, b) => {
			if (a.statusGroup !== b.statusGroup) {
				return a.statusGroup - b.statusGroup;
			}
			return b.spent - a.spent;
		});
	}
	return {
		active: sorted.filter((i) => i.statusGroup !== 1),
		fullySpent: sorted.filter((i) => i.statusGroup === 1),
	};
}

export type IncomeOccurrence = {
	date: string;
	label: string;
	category: string;
	amount: number;
};

export type IncomeBreakdownRow =
	| { kind: 'marker'; label: string }
	| {
			kind: 'occurrence';
			date: string;
			label: string;
			category: string;
			amount: number;
			/** What actually landed for this occurrence (see matchBreakdownActuals). */
			actual?: number;
			/** True when the landing was shared: one deposit covered several
			 * occurrences (e.g. salary + a one-off paid together) or several
			 * deposits filled one occurrence. */
			combined?: boolean;
	  };

export function addDays(date: Date, days: number): Date {
	const next = new Date(date);
	next.setDate(next.getDate() + days);
	return next;
}


export function isWithinRange(date: Date, start: Date, end: Date): boolean {
	return date >= start && date <= end;
}

export function getMonthlyPayDate(anchorMonth: Date, dayOfMonth: number): Date {
	const year = anchorMonth.getFullYear();
	const monthIndex = anchorMonth.getMonth();
	const monthEnd = new Date(year, monthIndex + 1, 0);
	const day = Math.min(Math.max(dayOfMonth, 1), monthEnd.getDate());
	const date = new Date(year, monthIndex, day);
	const weekday = date.getDay();
	if (weekday === 0) {
		return addDays(date, -2);
	}
	if (weekday === 6) {
		return addDays(date, -1);
	}
	return date;
}

export function getFortnightlyDatesInRange(anchor: Date, start: Date, end: Date): Date[] {
	return getRecurringDatesInRange(anchor, start, end, 14);
}

export function getFourWeeklyDatesInRange(anchor: Date, start: Date, end: Date): Date[] {
	return getRecurringDatesInRange(anchor, start, end, 28);
}

function getRecurringDatesInRange(anchor: Date, start: Date, end: Date, intervalDays: number): Date[] {
	const dates: Date[] = [];

	let forward = new Date(anchor);
	while (isWithinRange(forward, start, end)) {
		dates.push(new Date(forward));
		forward = addDays(forward, intervalDays);
	}

	let backward = addDays(anchor, -intervalDays);
	while (isWithinRange(backward, start, end)) {
		dates.push(new Date(backward));
		backward = addDays(backward, -intervalDays);
	}

	return dates.sort((a, b) => a.getTime() - b.getTime());
}

export function getWeeklyDatesInRange(start: Date, end: Date, weekday: number): Date[] {
	const dates: Date[] = [];
	const offset = (weekday - start.getDay() + 7) % 7;
	let current = addDays(start, offset);
	while (current <= end) {
		dates.push(new Date(current));
		current = addDays(current, 7);
	}
	return dates;
}

export function buildIncomeOccurrences(
	schedules: IncomeSchedule[],
	yearMonth: string
): IncomeOccurrence[] {
	const { start, end } = getPayCycleRange(yearMonth);
	const occurrences: IncomeOccurrence[] = [];

	for (const schedule of schedules) {
		const amount = schedule.amount ?? 0;
		if (!schedule.category?.trim() || amount === 0) continue;
		const label = schedule.name?.trim() || schedule.category.trim();
		const category = schedule.category.trim();

		if (schedule.frequency === 'monthly') {
			const day = schedule.monthlyDay ?? 15;
			const payDate = getMonthlyPayDate(start, day);
			if (isWithinRange(payDate, start, end)) {
				occurrences.push({ date: formatDate(payDate), label, category, amount });
			}
		}

		if (schedule.frequency === 'fortnightly' && schedule.fortnightlyPayDate) {
			const anchor = parseDateOnly(schedule.fortnightlyPayDate);
			if (isWithinRange(anchor, start, end)) {
				const dates = getFortnightlyDatesInRange(anchor, start, end);
				for (const date of dates) {
					occurrences.push({ date: formatDate(date), label, category, amount });
				}
			}
		}

		if (schedule.frequency === 'four-weekly' && schedule.fourWeeklyPayDate) {
			const anchor = parseDateOnly(schedule.fourWeeklyPayDate);
			if (isWithinRange(anchor, start, end)) {
				const dates = getFourWeeklyDatesInRange(anchor, start, end);
				for (const date of dates) {
					occurrences.push({ date: formatDate(date), label, category, amount });
				}
			}
		}

		if (schedule.frequency === 'weekly' && schedule.weeklyDay !== undefined) {
			const dates = getWeeklyDatesInRange(start, end, schedule.weeklyDay);
			for (const date of dates) {
				occurrences.push({ date: formatDate(date), label, category, amount });
			}
		}

		if (schedule.frequency === 'one-off' && schedule.oneOffDate) {
			const date = parseDateOnly(schedule.oneOffDate);
			if (isWithinRange(date, start, end)) {
				occurrences.push({ date: formatDate(date), label, category, amount });
			}
		}
	}

	return occurrences.sort((a, b) => a.date.localeCompare(b.date));
}

export function buildIncomeBreakdown(
	schedules: IncomeSchedule[],
	yearMonth: string
): IncomeBreakdownRow[] {
	const occurrences = buildIncomeOccurrences(schedules, yearMonth);
	if (occurrences.length === 0) return [];

	const today = new Date();
	const todayKey = formatDate(today);
	const { start, end } = getPayCycleRange(yearMonth);
	const isCurrent = today >= start && today <= end && yearMonth === getCurrentMonth();

	if (!isCurrent) {
		return occurrences.map((occurrence) => ({ ...occurrence, kind: 'occurrence' as const }));
	}

	const rows: IncomeBreakdownRow[] = [];
	let markerInserted = false;

	for (const occurrence of occurrences) {
		if (!markerInserted && occurrence.date >= todayKey) {
			rows.push({ kind: 'marker', label: 'Today' });
			markerInserted = true;
		}
		rows.push({ ...occurrence, kind: 'occurrence' });
	}

	if (!markerInserted) {
		rows.push({ kind: 'marker', label: 'Today' });
	}

	return rows;
}

/**
 * matchBreakdownActuals annotates breakdown occurrences with what actually
 * landed. Allocation model: income transactions in the occurrence's category
 * within ±windowDays (pay dates drift around weekends/holidays) form a pool
 * each occurrence draws from, nearest-by-date first, so a SINGLE combined
 * deposit can satisfy several occurrences (salary + a one-off cash-out paid
 * together) and several deposits can fill one occurrence. Any surplus left on
 * a deposit lands on its last consumer, so overs show exactly once. Shared
 * landings are flagged `combined`. Transfers, budget-excluded rows, and
 * non-positive amounts never participate. Comparing `actual` against `amount`
 * is what lets budgeted income be refined against reality.
 */
export function matchBreakdownActuals(
	rows: IncomeBreakdownRow[],
	transactions: Transaction[],
	windowDays = 4
): IncomeBreakdownRow[] {
	const dayMs = 24 * 60 * 60 * 1000;
	type Occ = Extract<IncomeBreakdownRow, { kind: 'occurrence' }>;
	const candidates = transactions
		.filter((t) => t.amount > 0 && !t.isTransfer && !t.excludeFromBudget)
		.map((t) => ({
			tx: t,
			time: new Date(t.date.slice(0, 10)).getTime(),
			remaining: t.amount,
			consumers: [] as Occ[],
		}));

	const out = rows.map((row) => (row.kind === 'occurrence' ? { ...row } : row));
	const sources = new Map<Occ, number>(); // deposits feeding each occurrence

	for (const row of out) {
		if (row.kind !== 'occurrence') continue;
		const at = new Date(row.date).getTime();
		let need = row.amount;
		while (need > 0.005) {
			let best: (typeof candidates)[number] | null = null;
			let bestDiff = Infinity;
			for (const c of candidates) {
				if (c.remaining <= 0.005 || c.tx.category !== row.category) continue;
				const diff = Math.abs(c.time - at);
				if (diff <= windowDays * dayMs && diff < bestDiff) {
					best = c;
					bestDiff = diff;
				}
			}
			if (!best) break;
			const take = Math.min(best.remaining, need);
			best.remaining = round2(best.remaining - take);
			need = round2(need - take);
			row.actual = round2((row.actual ?? 0) + take);
			best.consumers.push(row);
			sources.set(row, (sources.get(row) ?? 0) + 1);
		}
	}

	for (const c of candidates) {
		if (c.consumers.length === 0) continue;
		// Surplus on a consumed deposit shows once, on its last consumer.
		if (c.remaining > 0.005) {
			const last = c.consumers[c.consumers.length - 1];
			last.actual = round2((last.actual ?? 0) + c.remaining);
			c.remaining = 0;
		}
		if (c.consumers.length > 1) for (const o of c.consumers) o.combined = true;
	}
	for (const [occ, n] of sources) if (n > 1) occ.combined = true;

	return out;
}

export function alignFortnightlyPayDate(raw: string, range: { start: Date; end: Date }): string {
	return alignRecurringDate(raw, range, 14);
}

export function alignFourWeeklyPayDate(raw: string, range: { start: Date; end: Date }): string {
	return alignRecurringDate(raw, range, 28);
}

function alignRecurringDate(raw: string, range: { start: Date; end: Date }, intervalDays: number): string {
	const anchor = parseDateOnly(raw);
	if (!Number.isFinite(anchor.getTime())) {
		return formatDate(range.start);
	}

	let date = new Date(anchor);
	let guard = 0;
	while (date < range.start && guard < 60) {
		date = addDays(date, intervalDays);
		guard += 1;
	}
	if (date > range.end) {
		date = addDays(date, -intervalDays);
	}
	if (date < range.start || date > range.end) {
		return formatDate(range.start);
	}
	return formatDate(date);
}

export function getBudgetStatus(input: BudgetStatusInput): BudgetStatus {
	const { budgeted, spent, percentUsed, hasBudgetActivity, isNewBudget } = input;
	const isOverBudget = budgeted > 0 && spent > budgeted;
	const isUnbudgeted = budgeted === 0 && spent > 0;
	const isFullySpent =
		(budgeted > 0 && percentUsed >= 95 && percentUsed <= 100) ||
		(!isNewBudget && hasBudgetActivity && budgeted === 0 && spent === 0);
	const isUnderBudget = budgeted > 0 && percentUsed < 95 && !isNewBudget;

	let statusGroup: number;
	if (isOverBudget || isUnbudgeted) {
		statusGroup = 0;
	} else if (isFullySpent) {
		statusGroup = 1;
	} else {
		statusGroup = 2;
	}

	return {
		isOverBudget,
		isUnbudgeted,
		isFullySpent,
		isUnderBudget,
		statusGroup,
	};
}

// debtAccountIdsFromDebts returns the set of Akahu account IDs that belong
// to one of the user's debts — credit-card `cardAccountId` plus every
// mortgage tranche's `mortgageAccountIds[]`. Activity on these accounts is
// liability-side bookkeeping (card charges/credits/inbound payment legs,
// loan interest accruals, principal moves) — not the cash spend the budget
// tracks — and should be excluded from per-category aggregation.
export function debtAccountIdsFromDebts(
	debts: { debtType: string; cardAccountId?: string; mortgageAccountIds?: string[] }[]
): Set<string> {
	const ids = new Set<string>();
	for (const d of debts) {
		if (d.debtType === DEBT_TYPES.CREDIT_CARD && d.cardAccountId) {
			ids.add(d.cardAccountId);
		}
		if (d.debtType === DEBT_TYPES.MORTGAGE && d.mortgageAccountIds) {
			for (const id of d.mortgageAccountIds) {
				if (id) ids.add(id);
			}
		}
	}
	return ids;
}

export type MonthlyBudgetMode = 'spent-or-budgeted' | 'all-non-income';

export type MonthlyBudgetInput = {
	transactions: Transaction[];
	// The full debts list. Credit-card debts' cardAccountIds are derived
	// internally and passed to calculateCategorySummary so card-account activity
	// (the bookkeeping side of debt liability accounts) is uniformly excluded.
	debts: { debtType: string; cardAccountId?: string }[];
	categoryBudgets: Record<string, number>;
	isNewBudget: boolean;
	// Income categories — passed by the editor view to drop them entirely. The
	// dashboard leaves this unset and relies on its terminal
	// `budgeted > 0 || spent > 0` filter to hide income.
	incomeCategoryNames?: ReadonlySet<string>;
	// 'spent-or-budgeted' — categories with non-zero budget OR non-zero
	//   net-expense spend (dashboard behaviour).
	// 'all-non-income' — every name in `knownCategoryNames` ∪
	//   `categoryBudgets` minus `incomeCategoryNames` (budget-editor behaviour).
	mode: MonthlyBudgetMode;
	knownCategoryNames?: readonly string[];
};

// computeMonthlyBudget is the single source of truth for the dashboard's
// budget tiles and the budget editor's category rows. Both views feed it the
// same raw inputs (transactions + debts + budget config); it produces a
// per-category item with `budgeted`, `spent`, `count`, and the status flags
// from getBudgetStatus. Centralising the derivation means card-account
// exclusion, the `spent` clamping, and the `isOverBudget` check can't drift
// between pages.
export function computeMonthlyBudget(input: MonthlyBudgetInput): CategoryBudgetItem[] {
	const excludeAccountIds = debtAccountIdsFromDebts(input.debts);
	const summary = calculateCategorySummary(input.transactions, true, true, excludeAccountIds);
	const actualByCategory = new Map(summary.map((s) => [s.category, s] as const));

	const candidates = new Set<string>();
	if (input.mode === 'spent-or-budgeted') {
		for (const name of Object.keys(input.categoryBudgets)) {
			candidates.add(name);
		}
		for (const s of summary) {
			if (s.total < 0) candidates.add(s.category);
		}
	} else {
		const income = input.incomeCategoryNames;
		const seen = new Set<string>();
		const push = (name: string) => {
			if (seen.has(name)) return;
			if (income && income.has(name)) return;
			seen.add(name);
			candidates.add(name);
		};
		for (const name of input.knownCategoryNames ?? []) push(name);
		for (const name of Object.keys(input.categoryBudgets)) push(name);
	}

	// hasBudgetActivity feeds getBudgetStatus's "no spend yet" treatment so a
	// fresh month with no transactions doesn't flag every empty budget row as
	// fully spent.
	let totalBudget = 0;
	for (const v of Object.values(input.categoryBudgets)) totalBudget += v;
	const hasActualSpend = summary.some((s) => s.total < 0);
	const hasBudgetActivity = totalBudget > 0 || hasActualSpend;

	const items: CategoryBudgetItem[] = [];
	for (const name of candidates) {
		const budgeted = round2(input.categoryBudgets[name] ?? 0);
		const actual = actualByCategory.get(name);
		const isExpense = actual ? actual.total < 0 : budgeted > 0;
		const spent = round2(isExpense && actual ? Math.abs(actual.total) : 0);
		const percentUsed = budgeted > 0 ? round2((spent / budgeted) * 100) : spent > 0 ? 100 : 0;
		const status = getBudgetStatus({
			budgeted,
			spent,
			percentUsed,
			hasBudgetActivity,
			isNewBudget: input.isNewBudget,
		});
		items.push({
			name,
			budgeted,
			spent,
			count: actual?.count ?? 0,
			percentUsed,
			isUncategorised: name === UNCAT,
			...status,
		});
	}
	return items;
}
